import {
  TiptapEditor,
  TiptapEditorUpdatePayload,
} from 'client/components/core/tiptap-editor'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import { useManagement } from 'client/store'
import managementSelectors from 'client/store/management/managementSelectors'
import { updateEntity } from 'client/store/page/pageActions'
import { HeadlineInterface } from 'common/types/entities/HeadlineInterface'
import React from 'react'
import { useCalculateHeadingTypography } from 'src/editor/shared/hooks/use-calculate-heading-typography'
import { useThemeColor } from 'src/editor/shared/hooks/use-theme-color'
import BaseEntity from '../../core/BaseEntity/BaseEntityNew'

type HeadlineProps = {
  entity: HeadlineInterface
  isMoveUpAllowed: boolean
  isMoveDownAllowed: boolean
}

const Headline = ({
  entity,
  isMoveUpAllowed,
  isMoveDownAllowed,
}: HeadlineProps) => {
  const dispatch = useAppDispatch()
  const idEditing = useManagement(m =>
    managementSelectors.isEditing(m, entity.id),
  )
  const { isMobile } = useDeviceModeContext()
  const update = (payload: TiptapEditorUpdatePayload) => {
    dispatch(
      updateEntity({
        ...entity,
        ...payload,
      }),
    )
  }

  const {
    text: { headingTextColor, headingBackgroundColor },
    links: {
      linkTextColor,
      linkHoveredBackgroundColor,
      linkPressedBackgroundColor,
    },
  } = useThemeColor()

  const {
    calculatedFontSize,
    calculatedLineHeight,
    calculatedTextAlign,
    calculatedLetterSpacing,
    calculatedFontFamily,
    calculatedFontStyle,
    calculatedFontWeight,
  } = useCalculateHeadingTypography({
    fontSize: entity.fontSize,
    mobileFontSize: entity.mobileFontSize,
    lineHeight: entity.lineHeight,
    mobileLineHeight: entity.mobileLineHeight,
    textAlign: entity.textAlign,
    mobileTextAlign: entity.mobileTextAlign,
    letterSpacing: entity.letterSpacing,
    mobileLetterSpacing: entity.mobileLetterSpacing,
    fontFamily: entity.fontFamily,
    mobileFontFamily: entity.mobileFontFamily,
    fontWeight: entity.fontWeight,
    mobileFontWeight: entity.mobileFontWeight,
    fontStyle: entity.fontStyle,
    mobileFontStyle: entity.mobileFontStyle,
  })

  return (
    <BaseEntity
      entity={entity}
      isMoveUpAllowed={isMoveUpAllowed}
      isMoveDownAllowed={isMoveDownAllowed}
    >
      <TiptapEditor
        isEditing={idEditing}
        update={update}
        attrId={entity.htmlAttrId}
        padding={entity.padding}
        textAlign={
          isMobile ? calculatedTextAlign.mobile : calculatedTextAlign.desktop
        }
        textColor={headingTextColor}
        linkColor={linkTextColor}
        editorTextAlign={
          isMobile ? calculatedTextAlign.mobile : calculatedTextAlign.desktop
        }
        fontFamily={calculatedFontFamily.desktop}
        fontSize={calculatedFontSize.desktop}
        fontStyle={calculatedFontStyle.desktop}
        fontWeight={calculatedFontWeight.desktop}
        lineHeight={calculatedLineHeight.desktop}
        mobilePadding={entity.mobilePadding}
        mobileFontSize={calculatedFontSize.mobile}
        mobileLineHeight={calculatedLineHeight.mobile}
        mobileFontStyle={calculatedFontStyle.mobile}
        mobileFontFamily={calculatedFontFamily.mobile}
        mobileFontWeight={calculatedFontWeight.mobile}
        backgroundColor={entity.backgroundColor || headingBackgroundColor}
        mobileBackgroundColor={
          entity.mobileBackgroundColor || headingBackgroundColor
        }
        letterSpacing={calculatedLetterSpacing.desktop}
        mobileLetterSpacing={calculatedLetterSpacing.mobile}
        linkHover={{ color: linkHoveredBackgroundColor }}
        linkActive={{ color: linkPressedBackgroundColor }}
        content={entity.content || entity.rawContentState || ''}
        enableHeadings
      />
    </BaseEntity>
  )
}

export default Headline
